import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import apiFetch from "../../../../shared/services/apiClient";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import {
  buildChildrenOf, buildHidden, toggle,
  FiltrosBarra, CardsResumen, TableHeader, FilaCargando, FilaVacia, FilaArbol,
} from "./BalanceComprobacion";
import { usePDFIndividual } from "./EstadoFinanciero";

const fmt = (n) =>
  Math.abs(Number(n ?? 0)).toLocaleString("es-CR", { minimumFractionDigits: 2 });

// Signed format: shows negative sign and red color when n < 0
const fmtSigned = (n) => {
  const v = Number(n ?? 0);
  const abs = Math.abs(v).toLocaleString("es-CR", { minimumFractionDigits: 2 });
  return v < -0.005 ? `-₡${abs}` : `₡${abs}`;
};
const signColor = (n) => {
  const v = Number(n ?? 0);
  if (Math.abs(v) < 0.005) return "#6b7280";
  return v < 0 ? "#f87171" : "#4ade80";
};

const TIPO_COLORS = {
  ACTIVO:     "#60a5fa",
  PASIVO:     "#f87171",
  PATRIMONIO: "#c084fc",
};

function colorFila(fila) {
  const c = TIPO_COLORS[fila.tipo] ?? "#e5ecf5";
  if (fila.nivel === 1) return c;
  if (fila.nivel === 2) return "#e5ecf5";
  return fila.nivel === 3 ? "#d1d5db" : "#9ca3af";
}

function valorFila(fila) {
  const s = Number(fila.saldo ?? 0);
  if (Math.abs(s) < 0.01) return { text: "—", color: "#374151" };
  // Show signed value: negative saldo = red, positive = type color
  const typeColor = TIPO_COLORS[fila.tipo] ?? "#e5ecf5";
  const color = s < -0.005 ? "#f87171" : (fila.nivel <= 2 ? typeColor : "#9ca3af");
  return { text: fmtSigned(s), color };
}

function bgFila(fila) {
  const alpha = { 1: "12", 2: "08", 3: "transparent", 4: "transparent" };
  const TIPO_COLORS_BG = { ACTIVO:"#60a5fa", PASIVO:"#f87171", PATRIMONIO:"#c084fc" };
  if (fila.nivel >= 3) return "transparent";
  const c = TIPO_COLORS_BG[fila.tipo] ?? "#6b7280";
  return c + (fila.nivel === 1 ? "12" : "07");
}

function Separador({ label, color }) {
  return (
    <tr>
      <td colSpan={3} style={{ padding:"7px 14px",
        background: color + "22",
        borderLeft: `3px solid ${color}`,
        color, fontSize:"0.78rem", fontWeight:700,
        textTransform:"uppercase", letterSpacing:"0.8px" }}>
        {label}
      </td>
    </tr>
  );
}

function FilaTotal({ label, monto, color, big }) {
  const montoNum = Number(monto ?? 0);
  const montoColor = montoNum < -0.005 ? "#f87171" : color;
  return (
    <tr style={{ borderTop:`1px solid ${color}40` }}>
      <td colSpan={2} style={{ padding: big ? "10px 12px" : "7px 12px",
        paddingLeft:26, fontWeight:700, color, fontSize: big ? "0.88rem" : "0.83rem" }}>
        {label}
      </td>
      <td className="text-end" style={{ padding: big ? "10px 14px" : "7px 14px",
        fontFamily:"monospace", fontWeight:800, color: montoColor,
        fontSize: big ? "0.92rem" : "0.85rem" }}>
        {fmtSigned(monto)}
      </td>
    </tr>
  );
}

export default function BalanceGeneral() {
  const today = new Date().toISOString().split("T")[0];

  const [fechaHasta, setFechaHasta] = useState(today);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [soloConMov, setSoloConMov] = useState(true);
  const [collapsed,  setCollapsed]  = useState(new Set());
  const pdfBG = usePDFIndividual('/api/finance/reportes/balance-general-pdf/', 'situacion_financiera.pdf');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fechaHasta) qs.set("fecha_hasta", fechaHasta);
      const res = await apiFetch(`/api/finance/reportes/balance-general/?${qs}`);
      setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaHasta]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filas      = data?.filas ?? [];
  const childrenOf = buildChildrenOf(filas);
  const hidden     = buildHidden(filas, childrenOf, collapsed);

  const tieneMovArbol = (id) => {
    const f = filas.find(x => x.id === id);
    if (!f) return false;
    if (f.tiene_movimientos) return true;
    return (childrenOf[id] ?? []).some(tieneMovArbol);
  };

  const byTipo = (tipo) => filas.filter(f =>
    f.tipo === tipo && !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id))
  );

  const activos    = byTipo("ACTIVO");
  const pasivos    = byTipo("PASIVO");
  const patrimonio = byTipo("PATRIMONIO");
  const total      = activos.length + pasivos.length + patrimonio.length;

  const tA   = Number(data?.total_activo            ?? 0);
  const tP   = Number(data?.total_pasivo            ?? 0);
  const tPat = Number(data?.total_patrimonio        ?? 0);
  const tPP  = Number(data?.total_pasivo_patrimonio ?? 0);
  const bal  = data?.balanceado ?? true;
  const res  = Number(data?.resultado_ejercicio     ?? 0);
  const sup  = data?.es_superavit ?? true;

  return (
    <>
      
          <span className={invoiceStyles.inactive}>
            <Link to="/">
              <span className={invoiceStyles.inactive}>Inicio /</span>
            </Link>
              <span className={invoiceStyles.active}> Balance General</span>
          </span>
          <div>
            <h2 className="title mt-2 mb-2">Balance de general</h2>
            <p className="mb-3" style={{color:"#9ca3af"}}>Activos = Pasivos + Patrimonio al corte de fecha</p>
          </div>

      <FiltrosBarra
        fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
        soloConMov={soloConMov} setSoloConMov={setSoloConMov}
        onActualizar={fetchData} loading={loading}
        extraButtons={
          <>
            <button onClick={() => pdfBG.generar(`fecha_hasta=${fechaHasta}`)}
              disabled={pdfBG.loading || loading || !data}
              style={{ padding:'7px 14px', background:'rgba(59,130,246,0.15)',
                border:'1px solid rgba(59,130,246,0.5)', borderRadius:6,
                color:'#60a5fa', fontWeight:600, cursor:'pointer', fontSize:'0.85rem',
                opacity:(pdfBG.loading||!data)?0.5:1 }}>
              {pdfBG.loading
                ? <><i className='fa-solid fa-circle-notch fa-spin'/> Generando…</>
                : <><i className='fa-solid fa-file-pdf' style={{marginRight:5}}/>PDF</>}
            </button>
            {pdfBG.modal}
            {pdfBG.error && (
              <div style={{ padding:'6px 12px', background:'rgba(239,68,68,0.1)',
                border:'1px solid rgba(239,68,68,0.3)', borderRadius:6,
                color:'#f87171', fontSize:'0.8rem' }}>
                <i className='fa-solid fa-circle-exclamation' style={{marginRight:5}}/>
                {pdfBG.error}
              </div>
            )}
          </>
        }
      />

      {data && (
        <CardsResumen cards={[
          { label:"Total Activos",    value:Math.abs(tA),   prefix: tA   < 0 ? "-₡" : "₡", color: tA   < 0 ? "#f87171" : "#60a5fa", icon:"fa-building" },
          { label:"Total Pasivos",    value:Math.abs(tP),   prefix: tP   < 0 ? "-₡" : "₡", color: tP   < 0 ? "#f87171" : "#f87171", icon:"fa-file-invoice" },
          { label:"Total Patrimonio", value:Math.abs(tPat), prefix: tPat < 0 ? "-₡" : "₡", color: tPat < 0 ? "#f87171" : "#c084fc", icon:"fa-landmark" },
          { label: bal ? "Balanceado ✓" : "Diferencia ⚠",
            value: Math.abs(Number(data.diferencia)),
            color: bal ? "#4ade80" : "#fbbf24",
            icon:  bal ? "fa-scale-balanced" : "fa-triangle-exclamation" },
        ]}/>
      )}

      <div className={invoiceStyles["table-container"]}>
        <TableHeader
          count={total} loading={loading}
          onColapsar={() => setCollapsed(new Set(filas.filter(f => f.nivel < 4).map(f => f.id)))}
          onExpandir={() => setCollapsed(new Set())}
        />
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th style={{ width:130 }}>Código</th>
                <th>Nombre</th>
                <th className="text-end" style={{ width:160 }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? <FilaCargando colSpan={3} msg="Calculando balance general…"/>
                : total === 0
                  ? <FilaVacia colSpan={3} soloConMov={soloConMov} onMostrarTodas={() => setSoloConMov(false)}/>
                  : <>
                    {activos.length > 0 && <>
                      <Separador label="Activos" color="#60a5fa"/>
                      {activos.map(f => <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                        onToggle={id => setCollapsed(prev => toggle(prev, id))}
                        colorFn={colorFila} valorFn={valorFila} bgFn={bgFila}/>)}
                      <FilaTotal label="Total Activos" monto={tA} color="#60a5fa"/>
                    </>}
                    {pasivos.length > 0 && <>
                      <Separador label="Pasivos" color="#f87171"/>
                      {pasivos.map(f => <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                        onToggle={id => setCollapsed(prev => toggle(prev, id))}
                        colorFn={colorFila} valorFn={valorFila} bgFn={bgFila}/>)}
                      <FilaTotal label="Total Pasivos" monto={tP} color="#f87171"/>
                    </>}
                    {patrimonio.length > 0 && <>
                      <Separador label="Patrimonio" color="#c084fc"/>
                      {patrimonio.map(f => <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                        onToggle={id => setCollapsed(prev => toggle(prev, id))}
                        colorFn={colorFila} valorFn={valorFila} bgFn={bgFila}/>)}
                      <FilaTotal label="Total Patrimonio" monto={tPat} color="#c084fc"/>
                    </>}

                    {data && (
                      <>
                        <FilaTotal label="Total Pasivos + Patrimonio" monto={tPP} color="#9ca3af" big/>
                        <tr style={{ background: bal
                            ? "rgba(249,115,22,0.08)" : "rgba(251,191,36,0.08)",
                          borderTop:`3px solid ${bal ? "#f97316" : "#fbbf24"}` }}>
                          <td colSpan={2} style={{ padding:"12px 12px", fontWeight:800,
                            fontSize:"0.92rem", color: bal ? "#f97316" : "#fbbf24" }}>
                            {bal ? "✦ Activos = Pasivos + Patrimonio ✓" : "⚠ Diferencia — revisar asientos"}
                          </td>
                          <td className="text-end" style={{ padding:"12px 14px",
                            fontFamily:"monospace", fontWeight:800, fontSize:"1rem",
                            color: bal ? "#4ade80" : "#fbbf24" }}>
                            {bal ? "Balanceado" : fmtSigned(data.diferencia)}
                          </td>
                        </tr>
                      </>
                    )}
                  </>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
